import { ReactNode, createContext, useContext, useMemo, useReducer } from 'react';
import produce from 'immer';

import { useInsightsContext } from './InsightsContext';
import { unimplemented } from '@utils/commonUtils';
import { GARM_CATEGORIES } from '@insights_module/constants/garm';
import { CategoryType } from '@insights_module/types/BrandSafety';

type MainTableShowsColumnKeys = 'processed_episodes' | 'latest_published_episode';
type MainTableEpisodesColumnKeys = 'published_date' | 'processed_date';

export type MainTableColumnKeys =
  | CategoryType
  | MainTableShowsColumnKeys
  | MainTableEpisodesColumnKeys;

const MAIN_TABLE_SHOWS_COLUMN_KEYS = {
  PROCESSED_EPISODES: 'processed_episodes' as MainTableShowsColumnKeys,
  LATEST_PUBLISHED_EPISODE: 'latest_published_episode' as MainTableShowsColumnKeys,
};

const MAIN_TABLE_EPISODES_COLUMN_KEYS = {
  PUBLISHED_DATE: 'published_date' as MainTableEpisodesColumnKeys,
  PROCESSED_DATE: 'processed_date' as MainTableEpisodesColumnKeys,
};

type MainTableContextKeys =
  | 'insights.main_table.shows_columns'
  | 'insights.main_table.episodes_columns'
  | 'insights.main_table.garm_columns'
  | 'insights.main_table.include_unprocessed';

const MAIN_TABLE_CONTEXT_KEYS = {
  SHOWS_COLUMNS: 'insights.main_table.shows_columns' as MainTableContextKeys,
  EPISODES_COLUMNS: 'insights.main_table.episodes_columns' as MainTableContextKeys,
  GARM_COLUMNS: 'insights.main_table.garm_columns' as MainTableContextKeys,
  INCLUDE_UNPROCESSED: 'insights.main_table.include_unprocessed' as MainTableContextKeys,
};

export type ShowsColumns = {
  [K in MainTableShowsColumnKeys]: boolean;
};

export type EpisodesColumns = {
  [K in MainTableEpisodesColumnKeys]: boolean;
};

export type GarmColumns = {
  [K in CategoryType]: boolean;
};

type MainTableColumns = {
  [K in MainTableColumnKeys]: boolean;
};

type Action = {
  type: MainTableContextKeys;
  payload: boolean | GarmColumns | MainTableColumns;
};

export interface IMainTableContext {
  showsColumns: ShowsColumns;
  episodesColumns: EpisodesColumns;
  garmColumns: GarmColumns;
  gridTemplateColumns: string;
  includeUnprocessed: boolean;

  toggleColumn: (column: MainTableColumnKeys) => void;
  setIncludeUnprocessed: (includeUnprocessed: boolean) => void;
}

const initialState: IMainTableContext = {
  showsColumns: {
    [MAIN_TABLE_SHOWS_COLUMN_KEYS.PROCESSED_EPISODES]: true,
    [MAIN_TABLE_SHOWS_COLUMN_KEYS.LATEST_PUBLISHED_EPISODE]: true,
  } as ShowsColumns,
  episodesColumns: {
    [MAIN_TABLE_EPISODES_COLUMN_KEYS.PUBLISHED_DATE]: true,
    [MAIN_TABLE_EPISODES_COLUMN_KEYS.PROCESSED_DATE]: true,
  } as EpisodesColumns,
  garmColumns: {
    [GARM_CATEGORIES.adult_explicit_content]: true,
    [GARM_CATEGORIES.arms_ammunition]: true,
    [GARM_CATEGORIES.crime_harmful_act]: true,
    [GARM_CATEGORIES.death_injury_military_conflict]: true,
    [GARM_CATEGORIES.debated_social]: true,
    [GARM_CATEGORIES.hate_speech]: true,
    [GARM_CATEGORIES.illegal_drugs]: true,
    [GARM_CATEGORIES.obscenity]: true,
    [GARM_CATEGORIES.online_piracy]: true,
    [GARM_CATEGORIES.spam_harmful]: true,
    [GARM_CATEGORIES.terrorism]: true,
  } as GarmColumns,
  includeUnprocessed: false,
  gridTemplateColumns: '',
  toggleColumn: unimplemented,
  setIncludeUnprocessed: unimplemented,
};

const localShowsColumns = localStorage.getItem('main-table-shows-columns');
const localEpisodesColumns = localStorage.getItem('main-table-episodes-columns');
const localGarmColumns = localStorage.getItem('main-table-garm-columns');
const localIncludeUnprocessed = localStorage.getItem('main-table-include-unprocessed');
if (localShowsColumns) initialState.showsColumns = JSON.parse(localShowsColumns);
if (localEpisodesColumns) initialState.episodesColumns = JSON.parse(localEpisodesColumns);
if (localGarmColumns) initialState.garmColumns = JSON.parse(localGarmColumns);
if (localIncludeUnprocessed) initialState.includeUnprocessed = JSON.parse(localIncludeUnprocessed);

const reducer = produce((draft: IMainTableContext, action: Action) => {
  const { type, payload } = action;

  switch (type) {
    case MAIN_TABLE_CONTEXT_KEYS.SHOWS_COLUMNS: {
      const [key, value] = Object.entries(payload as MainTableColumns)[0];
      draft.showsColumns[key as MainTableShowsColumnKeys] = value;
      localStorage.setItem('main-table-shows-columns', JSON.stringify(draft.showsColumns));
      break;
    }

    case MAIN_TABLE_CONTEXT_KEYS.EPISODES_COLUMNS: {
      const [key, value] = Object.entries(payload as MainTableColumns)[0];
      draft.episodesColumns[key as MainTableEpisodesColumnKeys] = value;
      localStorage.setItem('main-table-episodes-columns', JSON.stringify(draft.episodesColumns));
      break;
    }

    case MAIN_TABLE_CONTEXT_KEYS.GARM_COLUMNS: {
      const [key, value] = Object.entries(payload as MainTableColumns)[0];
      draft.garmColumns[key as CategoryType] = value;
      localStorage.setItem('main-table-garm-columns', JSON.stringify(draft.garmColumns));
      break;
    }

    case MAIN_TABLE_CONTEXT_KEYS.INCLUDE_UNPROCESSED: {
      draft.includeUnprocessed = payload as boolean;
      localStorage.setItem(
        'main-table-include-unprocessed',
        JSON.stringify(draft.includeUnprocessed)
      );
      break;
    }

    default:
      break;
  }
}, initialState);

export const MainTableContext = createContext<IMainTableContext>(initialState);

export const MainTableProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { selectedTableType, selectedShow } = useInsightsContext();

  const gridTemplateColumns = useMemo(() => {
    const titleColumnTemplate = '280px';
    let episodesCountColumnTemplate = '';
    let latestPublishedColumnTemplate = '';
    let publishedDateColumnTemplate = '';
    let processedDateColumnTemplate = '';

    // if we have selected show, we are on show details screen and we see episodes table
    if (selectedTableType === 'shows' && !selectedShow && state.showsColumns.processed_episodes) {
      episodesCountColumnTemplate = '105px';
    }
    if (
      selectedTableType === 'shows' &&
      !selectedShow &&
      state.showsColumns.latest_published_episode
    ) {
      latestPublishedColumnTemplate = '135px';
    }
    if (
      (selectedTableType === 'episodes' || !!selectedShow) &&
      state.episodesColumns.published_date
    ) {
      publishedDateColumnTemplate = '110px';
    }
    if (
      (selectedTableType === 'episodes' || !!selectedShow) &&
      state.episodesColumns.processed_date
    ) {
      processedDateColumnTemplate = '110px';
    }

    const garmColumnsNumber = Object.values(state.garmColumns).filter(
      (showColumn) => showColumn
    ).length;

    // separator between garm columns and other columns,
    // it's 36px if we have garm columns and at least one of the columns to the left of the separator is visible
    // or it's auto if there are no garm columns to push table actions to the right
    const separatorColumnTemplate =
      ((selectedTableType === 'shows' && !selectedShow && state.showsColumns.processed_episodes) ||
        (selectedTableType === 'shows' &&
          !selectedShow &&
          state.showsColumns.latest_published_episode) ||
        ((selectedTableType === 'episodes' || !!selectedShow) &&
          state.episodesColumns.published_date) ||
        ((selectedTableType === 'episodes' || !!selectedShow) &&
          state.episodesColumns.processed_date)) &&
      garmColumnsNumber
        ? '36px'
        : 'auto';

    const garmColumnsTemplate = garmColumnsNumber
      ? `repeat(${garmColumnsNumber.toString()}, minmax(106px, 200px))`
      : '';

    const lastColumnTemplate = '50px';

    return `grid-template-columns: ${titleColumnTemplate} ${episodesCountColumnTemplate} ${latestPublishedColumnTemplate} ${publishedDateColumnTemplate} ${processedDateColumnTemplate} ${separatorColumnTemplate} ${garmColumnsTemplate} auto ${lastColumnTemplate}`;
  }, [
    state.garmColumns,
    selectedTableType,
    state.showsColumns,
    state.episodesColumns,
    selectedShow,
  ]);

  function toggleColumn(column: MainTableColumnKeys) {
    const showsColumn = state.showsColumns[column as MainTableShowsColumnKeys];
    if (typeof showsColumn !== 'undefined') {
      dispatch({
        type: MAIN_TABLE_CONTEXT_KEYS.SHOWS_COLUMNS,
        payload: { [column]: !showsColumn } as MainTableColumns,
      });
      return;
    }

    const episodesColumn = state.episodesColumns[column as MainTableEpisodesColumnKeys];
    if (typeof episodesColumn !== 'undefined') {
      dispatch({
        type: MAIN_TABLE_CONTEXT_KEYS.EPISODES_COLUMNS,
        payload: { [column]: !episodesColumn } as MainTableColumns,
      });
    }

    const garmColumn = state.garmColumns[column as CategoryType];
    if (typeof garmColumn !== 'undefined') {
      dispatch({
        type: MAIN_TABLE_CONTEXT_KEYS.GARM_COLUMNS,
        payload: { [column]: !garmColumn } as MainTableColumns,
      });
    }
  }

  function setIncludeUnprocessed(includeUnprocessed: boolean) {
    dispatch({
      type: MAIN_TABLE_CONTEXT_KEYS.INCLUDE_UNPROCESSED,
      payload: includeUnprocessed,
    });
  }

  const value = {
    ...state,
    toggleColumn,
    gridTemplateColumns,
    setIncludeUnprocessed,
  };

  return <MainTableContext.Provider value={value}>{children}</MainTableContext.Provider>;
};

export const useMainTableContext = () => useContext(MainTableContext);
